import { useWorkspace } from '@/api';
import { useStudioContour } from '@/api/studio';
import type { ContourInfo, LegalSchema, LegalValues, Money } from '@/api/types';

// Everything that differs between the RU contour and the main one comes from
// the contour config through the API (workspace.contour, /api/studio/contour):
// requisites, acts, tax wording, the transfer QR. Pages ask here, not `code === 'ru'`.

/** The signed-in client's contour; undefined while the workspace loads. */
export const useContour = (): ContourInfo | undefined => useWorkspace().data?.contour;

/** Acts exist only where the contour has them (RU). Unknown yet — none, so «акт» never flashes in main. */
export const hasActs = (c?: ContourInfo) => !!c?.legal.acts;

const providers = { tochka: { page: 'банка Точка', who: 'Банк' }, paddle: { page: 'Paddle', who: 'Paddle' }, none: { page: 'оплаты', who: 'Банк' } } as const;
/** Who takes the payment, for «страница оплаты …» and «… подтверждает платёж». */
export const providerOf = (c?: ContourInfo) => providers[c?.payments.provider ?? 'none'] ?? providers.none;

/** «₽», «$» — the sign of a currency as the Russian UI prints it. */
export function currencySign(currency: Money['currency']): string {
  try {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

let regions: Intl.DisplayNames | undefined;
/** Country name in Russian by its ISO alpha-2 code. */
export function countryName(code: string): string {
  if (!code) return '';
  try {
    regions ??= new Intl.DisplayNames(['ru'], { type: 'region' });
    return regions.of(code) ?? code;
  } catch {
    return code;
  }
}

/** Fill a wording template: «Оплата по счёту № {number} от {date}». */
export const fill = (template: string, vars: Record<string, string>) => template.replace(/\{(\w+)\}/g, (m, k: string) => vars[k] ?? m);

/** «ИНН 7800000000», «VAT / Tax ID —»: the tax identifier the contour mirrors into the client's tax id. */
export function taxIdLine(legal: LegalSchema, values: LegalValues): string {
  const f = legal.fields.find((x) => x.column === 'tax_id');
  return f ? `${f.label} ${values[f.key] || '—'}` : '';
}

/** Empty values for every field of the schema, then what is known. */
export const blankLegal = (legal: LegalSchema, values?: LegalValues): LegalValues => Object.fromEntries(legal.fields.map((f) => [f.key, values?.[f.key] ?? '']));

/** The studio's view of its installation's contour: currency for prices it types, its sign, whether acts exist. */
export function useStudioTerms() {
  const { data: contour } = useStudioContour();
  const currency: Money['currency'] = contour?.currency ?? 'RUB';
  // No sign until the contour is known: «₽» must not flash in the main contour.
  return { contour, currency, sign: contour ? currencySign(contour.currency) : '', acts: hasActs(contour), legal: contour?.legal };
}

/** «Стоимость, ₽» — or just «Стоимость» while the contour loads. */
export const withSign = (label: string, sign: string) => (sign ? `${label}, ${sign}` : label);
